import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Users, Loader2, Pencil, TrendingUp, BarChart3 } from 'lucide-react';
import { ApercuActionCard } from '../components/apercu/ApercuActionCard';
import { FilChronoLigne, LigneDepliable, TuileLiee } from '../components/apercu/ApercuLignes';
import { SuiviCertification } from '../components/apercu/SuiviCertification';
import { SuiviPedagogique } from '../components/suivi/SuiviPedagogique';
import { CandidatsListe } from '../components/entreprise/CandidatsListe';
import { FicheCandidatEntreprise } from '../components/entreprise/FicheCandidatEntreprise';
import { CandidatFicheActions } from '../components/candidat/CandidatFicheActions';
import { AftsManquantesInline } from '../components/aft/AftsManquantesInline';
import { StatsBpf } from '../components/apercu/StatsBpf';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Aperçu d'une action de formation (admin) : tuile, sections, candidats, fil chronologique, AFT liées
export default function DossierApercuPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [apercu, setApercu] = useState(null);
  const [erreur, setErreur] = useState('');
  const [fiche, setFiche] = useState(null);

  const [reload, setReload] = useState(0);
  useEffect(() => {
    setApercu(null);
    axios.get(`${API_URL}/portail/admin/dossier/${id}/apercu`).then(r => setApercu(r.data)).catch(e => setErreur(e.response?.data?.detail || 'Action introuvable'));
  }, [id, reload]);

  const action = apercu?.action;
  if (apercu?.type_dossier === 'aft') return <Navigate to={`/dossiers/${id}/edit`} replace />;
  const nbAlertes = action?.candidats.filter(c => c.en_difficulte || c.nb_alertes_formateur > 0).length || 0;
  const nbAbandons = action?.candidats.filter(c => c.abandon).length || 0;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4" data-testid="dossier-apercu-page">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1 flex-wrap">
            {apercu?.af_mere ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => navigate(`/dossiers/${apercu.af_mere.id}`)} data-testid="apercu-retour-af-mere"><ArrowLeft className="w-4 h-4 mr-1" /> AF mère {apercu.af_mere.code}</Button>
            ) : (
              <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/dossiers')} data-testid="apercu-retour-liste"><ArrowLeft className="w-4 h-4 mr-1" /> Actions de formation</Button>
            )}
            {apercu?.af_mere && <Button type="button" variant="ghost" size="sm" className="text-slate-500" onClick={() => navigate('/dossiers')} data-testid="apercu-retour-liste">Toutes les actions</Button>}
          </div>
          {action && <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/dossiers/${id}/edit?from=apercu`)} data-testid="apercu-modifier-header"><Pencil className="w-4 h-4 mr-1" /> Modifier</Button>}
        </div>
        {erreur && <p className="text-sm text-red-600" data-testid="apercu-erreur">{erreur}</p>}
        {!apercu && !erreur && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>}
        {action && (
          <>
            <ApercuActionCard action={action} apercu={apercu} dossierId={id} />
            <LigneDepliable icon={Users} testId="apercu-candidats" defaultOpen={searchParams.get('section') === 'candidats'} titre={`${action.candidats.length} candidat(s)`}
              resume={<span className="flex items-center gap-2 text-xs font-normal">{nbAlertes > 0 && <span className="text-red-600">⚠ {nbAlertes} en difficulté / alerte</span>}{nbAbandons > 0 && <span className="text-slate-500">↪ {nbAbandons} abandon(s)</span>}</span>}
              droite={<span>Voir les candidats</span>}>
              <CandidatsListe candidats={action.candidats} dossierId={id} accent="indigo" showEntreprise onOpen={(cid) => setFiche({ candidat_id: cid })}
                renderActions={(c) => <CandidatFicheActions dossierId={id} candidatId={c.candidat_id} nom={c.nom} prenom={c.prenom} apiBase={`${API_URL}/portail/admin`} editUrl={`/dossiers/${id}/candidat/${c.candidat_id}?from=apercu`} />} />
            </LigneDepliable>
            <FilChronoLigne action={action} dossierId={id} />
            {apercu.type_dossier !== 'aft' && (
              <LigneDepliable icon={TrendingUp} testId="apercu-suivi-pedagogique" titre="Suivi pédagogique" defaultOpen={searchParams.get('section') === 'suivi'}
                resume={<span className="text-xs font-normal text-slate-500">Programme réalisé / à faire, dépassements, QCM & évaluations à la date du jour</span>} droite={<span>Voir l'avancement</span>}>
                <SuiviPedagogique dossierId={id} canManage={['admin', 'super_admin', 'responsable_pedagogique'].includes(user?.role)} />
              </LigneDepliable>
            )}
            {apercu.type_dossier !== 'aft' && <SuiviCertification dossierId={id} onOpenCandidat={(cid) => setFiche({ candidat_id: cid })} />}
            {apercu.afts.map(a => <TuileLiee key={a.id} aft={a} nbTotal={apercu.nb_candidats_total} nbDelegues={apercu.nb_delegues_aft} />)}
            {apercu.type_dossier !== 'aft' && <AftsManquantesInline dossierId={id} onCreated={() => setReload(v => v + 1)} />}
            {apercu.type_dossier !== 'aft' && !apercu.af_mere && (
              <LigneDepliable icon={BarChart3} testId="apercu-bpf" titre="Statistiques BPF" defaultOpen={searchParams.get('section') === 'bpf'}
                resume={<span className="text-xs font-normal text-slate-500">Heures-stagiaires réellement suivies pour le Bilan pédagogique et financier</span>} droite={<span>Voir les statistiques</span>}>
                <StatsBpf dossierId={id} />
              </LigneDepliable>
            )}
            {apercu.af_mere && <TuileLiee afMere={apercu.af_mere} nbTotal={apercu.nb_candidats_total} />}
          </>
        )}
        <FicheCandidatEntreprise open={!!fiche} candidatId={fiche?.candidat_id} dossierId={id} onClose={() => setFiche(null)} apiBase={`${API_URL}/portail/admin`}
          onEdit={fiche ? () => navigate(`/dossiers/${id}/candidat/${fiche.candidat_id}?from=apercu`) : undefined} />
      </div>
    </DashboardLayout>
  );
}
